import { reactive } from 'vue'
import appNotificationService from "./utils/appNotificationService";

class NetworkService {
    constructor() {
        this.state = reactive({
            isOnline: this.getInitialOnlineStatus(),
            isConnected: this.getInitialOnlineStatus(),
            connectionType: this.getConnectionType(),
            lastOnline: this.getInitialOnlineStatus() ? Date.now() : null,
            lastOffline: !this.getInitialOnlineStatus() ? Date.now() : null,
            forceOffline: false
        })

        // Attendre que Cordova soit prêt
        if (window.cordova) {
            document.addEventListener('deviceready', () => {
                this.setupListeners()
                this.startConnectionCheck()
            }, false)
        } else {
            this.setupListeners()
            this.startConnectionCheck()
        }
    }

    getInitialOnlineStatus() {
        // Cordova
        if (window.cordova && navigator.connection) {
            return navigator.connection.type !== 'none'
        }
        // Web/PWA
        return navigator.onLine
    }

    setupListeners() {
        // Cordova utilise document.addEventListener au lieu de window
        const target = window.cordova ? document : window

        target.addEventListener('online', () => {
            this.state.isOnline = true
            this.state.lastOnline = Date.now()
            this.state.connectionType = this.getConnectionType()
            console.log('🟢 Network: ONLINE')

            // Vérifier la vraie connexion
            this.checkRealConnection()
        })

        target.addEventListener('offline', () => {
            this.state.isOnline = false
            this.state.isConnected = false
            this.state.lastOffline = Date.now()
            console.log('🔴 Network: OFFLINE')
        })

        // Pour Cordova : écouter les changements de type de connexion
        if (window.cordova && navigator.connection) {
            const Connection = window.Connection || {}

            // Vérifier périodiquement le type de connexion (fallback)
            setInterval(() => {
                const newType = this.getConnectionType()
                if (newType !== this.state.connectionType) {
                    console.log('🔄 Connection type changed:', this.state.connectionType, '→', newType)
                    this.state.connectionType = newType

                    // Si passage à "none", déclencher offline
                    if (newType === 'none' && this.state.isOnline) {
                        this.state.isOnline = false
                        this.state.isConnected = false
                        this.state.lastOffline = Date.now()
                        console.log('🔴 Network: OFFLINE (detected by polling)')
                    }
                    // Si passage de "none" à autre chose, déclencher online
                    else if (newType !== 'none' && !this.state.isOnline) {
                        this.state.isOnline = true
                        this.state.lastOnline = Date.now()
                        console.log('🟢 Network: ONLINE (detected by polling)')
                        this.checkRealConnection()
                    }
                }
            }, 2000) // Vérifier toutes les 2 secondes
        }
    }

    getConnectionType() {
        // Cordova Network Information Plugin
        if (window.cordova && navigator.connection) {
            const type = navigator.connection.type
            console.log('📡 Cordova connection type:', type)

            // Mapper les types Cordova vers des noms lisibles
            const typeMap = {
                'none': 'none',
                'unknown': 'unknown',
                'ethernet': 'ethernet',
                'wifi': 'wifi',
                '2g': '2g',
                '3g': '3g',
                '4g': '4g',
                'cellular': 'cellular',
                'cell': 'cellular'
            }

            return typeMap[type] || type
        }

        // Web/PWA Network Information API
        if (navigator.connection) {
            return navigator.connection.effectiveType || 'unknown'
        }

        return 'unknown'
    }

    setForceOffline(offline) {
        this.state.forceOffline = offline
        console.log('🔧 Force offline:', offline)
    }

    // Test de connectivité réelle (pas juste navigator.onLine)
    async checkRealConnection() {
        // Ne pas tester si on sait qu'on est offline
        if (!this.state.isOnline) {
            this.state.isConnected = false
            return false
        }

        try {
            // Utiliser window.environment pour l'URL de l'API
            const baseUrl = window.environment?.API_URL || 'https://pappus.app'

            // Essayer de ping l'API
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), 5000)

            const response = await fetch(`${baseUrl}/api/health`, {
                method: 'HEAD',
                cache: 'no-cache',
                signal: controller.signal
            })

            clearTimeout(timeoutId)

            const isConnected = response.ok
            this.state.isConnected = isConnected

            if (!isConnected && this.state.isOnline) {
                console.log('⚠️ Network: Browser says online but no real connection')
            } else if (isConnected) {
                console.log('✅ Network: Real connection verified')
            }

            return isConnected
        } catch (error) {
            console.log('❌ Network: Connection check failed:', error.message)
            this.state.isConnected = false
            return false
        }
    }

    startConnectionCheck() {
        // Vérifier la vraie connectivité toutes les 30 secondes
        setInterval(() => {
            if (this.state.isOnline) {
                this.checkRealConnection()
            }
        }, 30000)

        // Vérification initiale après 2 secondes
        setTimeout(() => {
            if (this.state.isOnline) {
                this.checkRealConnection()
            }
        }, 2000)
    }

    // Getters réactifs
    get isOnline() {
        return this.state.isOnline && !this.state.forceOffline
    }

    get isConnected() {
        return this.state.isConnected && !this.state.forceOffline
    }

    get status() {
        return this.state
    }
}

export default new NetworkService()